import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export interface DevSettingsState {
  devModeUnlocked: boolean;
  /** Enables experimental network quality tiles layer controls in map UI. */
  devNetworkTilesEnabled: boolean;
  /** Preview SPV harbor voting UI (session-persisted). */
  devSpvVotingEnabled: boolean;
}

interface DevSettingsContextValue extends DevSettingsState {
  setDevModeUnlocked: (unlocked: boolean) => void;
  setDevNetworkTilesEnabled: (enabled: boolean) => void;
  setDevSpvVotingEnabled: (enabled: boolean) => void;
}

const DevSettingsContext = createContext<DevSettingsContextValue | null>(null);

const SESSION_KEY = "devSettings";

function loadInitialState(): DevSettingsState {
  if (typeof window === "undefined") {
    return {
      devModeUnlocked: false,
      devNetworkTilesEnabled: false,
      devSpvVotingEnabled: false,
    };
  }

  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) {
      return {
        devModeUnlocked: false,
        devNetworkTilesEnabled: false,
        devSpvVotingEnabled: false,
      };
    }
    const parsed = JSON.parse(raw) as Partial<DevSettingsState> | null;
    return {
      devModeUnlocked: Boolean(parsed?.devModeUnlocked),
      devNetworkTilesEnabled: Boolean(parsed?.devNetworkTilesEnabled),
      devSpvVotingEnabled: Boolean(parsed?.devSpvVotingEnabled),
    };
  } catch {
    return {
      devModeUnlocked: false,
      devNetworkTilesEnabled: false,
      devSpvVotingEnabled: false,
    };
  }
}

function persistState(state: DevSettingsState) {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // ignore storage failures
  }
}

export function DevSettingsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<DevSettingsState>(() => loadInitialState());

  const setDevModeUnlocked = useCallback((unlocked: boolean) => {
    setState((prev) => {
      const next = { ...prev, devModeUnlocked: unlocked };
      persistState(next);
      return next;
    });
  }, []);

  const setDevNetworkTilesEnabled = useCallback((enabled: boolean) => {
    setState((prev) => {
      const next = { ...prev, devNetworkTilesEnabled: enabled };
      persistState(next);
      return next;
    });
  }, []);

  const setDevSpvVotingEnabled = useCallback((enabled: boolean) => {
    setState((prev) => {
      const next = { ...prev, devSpvVotingEnabled: enabled };
      persistState(next);
      return next;
    });
  }, []);

  const value = useMemo<DevSettingsContextValue>(
    () => ({
      ...state,
      setDevModeUnlocked,
      setDevNetworkTilesEnabled,
      setDevSpvVotingEnabled,
    }),
    [
      state,
      setDevModeUnlocked,
      setDevNetworkTilesEnabled,
      setDevSpvVotingEnabled,
    ],
  );

  return (
    <DevSettingsContext.Provider value={value}>
      {children}
    </DevSettingsContext.Provider>
  );
}

export function useDevSettings(): DevSettingsContextValue {
  const ctx = useContext(DevSettingsContext);
  if (!ctx) {
    throw new Error("useDevSettings must be used within DevSettingsProvider");
  }
  return ctx;
}
